/**
 * members.relationship_strength — single-member relationship card
 * (FB-04LY0RA5). Shows tier + score + touch count + last touch for one
 * specific bioguide. Useful pinned to the dashboard for "are we still
 * close to Senator Lee?"-style watchpoints.
 *
 * Params: bioguide_id (required).
 */
import { useQuery } from "@tanstack/react-query";
import { DataUnavailable } from "../../../framework/react/DataUnavailable.js";
import { Link } from "react-router-dom";
import { WidgetProps } from "../registry";

interface Row {
  bioguide_id: string;
  display_name?: string;
  party?: string;
  state?: string;
  district?: number | null;
  chamber?: string;
  relationship_tier: "hot" | "warm" | "cool" | "cold" | "never_engaged";
  relationship_score: number;
  touch_count: number;
  last_touch_at: string | null;
  linked_contacts: Array<{ contact_id: string; display_name?: string | null; role?: string | null }>;
  recent_engagements: Array<{ id: string; engagement_type?: string | null; occurred_at?: string | null; summary?: string | null }>;
}

const TIER_COLOR: Record<string, string> = {
  hot: "bg-red-500",
  warm: "bg-orange-500",
  cool: "bg-sky-500",
  cold: "bg-stone-400",
  never_engaged: "bg-stone-300",
};

function relativeDays(d: string | null | undefined): string {
  if (!d) return "never";
  const days = Math.floor((Date.now() - new Date(d).getTime()) / 86400000);
  if (days === 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 60) return `${days}d ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}

export function MembersRelationshipStrength({ spec, workspaceId }: WidgetProps) {
  const bioguide = String(spec.params?.bioguide_id ?? "").trim();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["widget", "members.relationship_strength", workspaceId, bioguide],
    queryFn: async () => {
      const res = await fetch(
        `/api/v1/workspaces/${workspaceId}/top-relationships?limit=200`,
        { credentials: "include" }
      );
      if (!res.ok) throw new Error(await res.text());
      const json = await res.json();
      const rows = (json.members ?? []) as Row[];
      return rows.find((m) => m.bioguide_id === bioguide) ?? null;
    },
    enabled: !!bioguide,
  });

  if (!bioguide) return <div className="text-xs text-stone-600">Set <code>params.bioguide_id</code> to a member to track.</div>;
  if (error) return <DataUnavailable message="Relationship strength could not be loaded." retry={refetch} />;
  if (isLoading) return <div role="status" className="space-y-1.5"><span className="sr-only">Loading…</span><span aria-hidden="true" className="cmcp-skeleton h-3.5 w-3/4" /><span aria-hidden="true" className="cmcp-skeleton h-3.5 w-1/2" /><span aria-hidden="true" className="cmcp-skeleton h-3.5 w-2/3" /></div>;
  if (!data) return <div className="text-xs text-stone-500">No touches logged for {bioguide} yet.</div>;

  const dot = TIER_COLOR[data.relationship_tier] ?? "bg-stone-300";
  return (
    <div className="space-y-2 text-xs">
      <div className="flex items-center gap-2">
        <span className={`w-2.5 h-2.5 rounded-full ${dot}`} />
        <Link to={`/members/${encodeURIComponent(data.bioguide_id)}`} className="font-semibold text-ink hover:underline flex-1 truncate">
          {data.display_name ?? data.bioguide_id}
        </Link>
        <span className="text-stone-500 font-mono text-[10px]">{data.state}{data.district ? `-${data.district}` : ""}</span>
      </div>
      <div className="grid grid-cols-3 gap-1.5 text-center">
        <div className="bg-stone-50 border border-stone-100 rounded-md p-1.5">
          <div className="text-[10px] uppercase tracking-wider text-stone-600 font-semibold">Tier</div>
          <div className="font-bold text-sm capitalize">{data.relationship_tier.replace("_", " ")}</div>
        </div>
        <div className="bg-stone-50 border border-stone-100 rounded-md p-1.5">
          <div className="text-[10px] uppercase tracking-wider text-stone-600 font-semibold">Score</div>
          <div className="font-bold text-sm tabular-nums">{data.relationship_score.toFixed(1)}</div>
        </div>
        <div className="bg-stone-50 border border-stone-100 rounded-md p-1.5">
          <div className="text-[10px] uppercase tracking-wider text-stone-600 font-semibold">Touches</div>
          <div className="font-bold text-sm tabular-nums">{data.touch_count}</div>
        </div>
      </div>
      <div className="text-stone-600 text-[11px]">Last touch: {relativeDays(data.last_touch_at)} · {data.linked_contacts.length} office contact{data.linked_contacts.length === 1 ? "" : "s"}</div>
    </div>
  );
}
